import React, { useRef, useState } from 'react';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import '../assets/styles/pullToRefresh.css';

/**
 * Pull-to-refresh for pages inside MainLayout.
 * The wrapper is deliberately not scrollable; .main-content remains the only
 * scroll container and this component reads that container's real scrollTop.
 */
const PullToRefresh = ({ onRefresh, children, className = '' }) => {
  const wrapperRef = useRef(null);
  const gestureRef = useRef(null);
  const refreshingRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const directionThreshold = 12;
  const refreshThreshold = 80;
  const longPressThreshold = 500;

  const resetGesture = () => {
    gestureRef.current = null;
    setPullDistance(0);
  };

  const handleTouchStart = (event) => {
    if (refreshingRef.current || event.touches.length !== 1) return;

    const target = event.target;
    if (target.closest('.ant-dropdown, .ant-modal, .ant-picker-dropdown, .ant-popover, .ant-select-dropdown')) {
      return;
    }

    const scrollContainer = wrapperRef.current?.closest('.main-content');
    if (!scrollContainer || scrollContainer.scrollTop > 1) return;

    const touch = event.touches[0];
    gestureRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startedAt: Date.now(),
      scrollContainer,
      intent: null,
      distance: 0,
    };
  };

  const handleTouchMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;

    // Only a prompt, predominantly downward gesture that started at the top
    // may become a refresh. All upward, horizontal and long-press gestures are
    // released to the browser's native scrolling immediately.
    if (
      gesture.scrollContainer.scrollTop > 1 ||
      Date.now() - gesture.startedAt >= longPressThreshold ||
      deltaY <= -directionThreshold ||
      Math.abs(deltaX) > Math.max(Math.abs(deltaY), directionThreshold)
    ) {
      resetGesture();
      return;
    }

    if (gesture.intent === null && deltaY < directionThreshold) return;

    gesture.intent = 'pull';
    gesture.distance = deltaY;
    setPullDistance(deltaY);
    event.preventDefault();
  };

  const runRefresh = async () => {
    if (refreshingRef.current || typeof onRefresh !== 'function') return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  };

  const handleTouchEnd = () => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    setPullDistance(0);

    if (
      gesture?.intent === 'pull' &&
      gesture.distance >= refreshThreshold &&
      Date.now() - gesture.startedAt < longPressThreshold
    ) {
      void runRefresh();
    }
  };

  const indicatorHeight = refreshing
    ? 48
    : Math.min(pullDistance * 0.5, refreshThreshold);

  return (
    <div
      className={`pull-refresh-container ${className}`}
      ref={wrapperRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={resetGesture}
    >
      {(pullDistance > 0 || refreshing) && (
        <div
          className="custom-pull-indicator"
          style={{ height: indicatorHeight }}
          aria-live="polite"
        >
          {refreshing ? <LoadingOutlined spin /> : <DownOutlined />}
          <span>
            {refreshing
              ? 'Refreshing...'
              : pullDistance >= refreshThreshold
                ? 'Release to refresh'
                : 'Pull to refresh'}
          </span>
        </div>
      )}
      {children}
    </div>
  );
};

export default PullToRefresh;
